import { useState, useEffect, useCallback, useMemo } from "react";
import { useLazyQuery } from "@apollo/client";
import { GENERATE_START_MESSAGE_WITH_TRACKING } from "../services/graphql/start-message-operations";
import type { StartMessageWithTracking } from "../types/start-message-types";

interface UseRealTimePreviewOptions {
  content: string;
  messageId?: string;
  conversationId?: string;
  userId?: string;
  updateInterval?: number;
  enableRealTimeUpdates?: boolean;
}

interface PreviewData {
  smsPreview: string;
  chatPreview: string;
  trackingData?: string;
  trackingLink?: string;
  fullContent: string;
  isValid: boolean;
}

interface UseRealTimePreviewResult {
  previewData: PreviewData;
  isGenerating: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refreshPreview: () => void;
}

export function useRealTimePreview(
  options: UseRealTimePreviewOptions
): UseRealTimePreviewResult {
  const {
    content,
    messageId,
    conversationId,
    userId,
    updateInterval = 500, // Update every 500ms to avoid too frequent API calls
    enableRealTimeUpdates = true,
  } = options;

  const [previewData, setPreviewData] = useState<PreviewData>({
    smsPreview: "",
    chatPreview: "",
    fullContent: content,
    isValid: false,
  });
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [updateTimeout, setUpdateTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // GraphQL query for generating preview with tracking
  const [generatePreview, { loading: isGenerating, error: queryError }] =
    useLazyQuery(GENERATE_START_MESSAGE_WITH_TRACKING, {
      errorPolicy: "all",
      onCompleted: (data) => {
        if (data?.generateStartMessageWithTracking) {
          const trackingData: StartMessageWithTracking =
            data.generateStartMessageWithTracking;

          setPreviewData({
            smsPreview: trackingData.previewSMS,
            chatPreview: trackingData.previewChat,
            trackingData: trackingData.trackingData,
            trackingLink: trackingData.trackingLink || undefined,
            fullContent: trackingData.fullContent,
            isValid: true,
          });
          setLastUpdated(new Date());
        }
      },
      onError: (error) => {
        console.error("Error generating preview:", error);
        // Generate fallback preview without tracking
        generateFallbackPreview();
      },
    });

  // Generate fallback preview without tracking data
  const generateFallbackPreview = useCallback(() => {
    const sampleTrackingData = "ABC123-XYZ789";
    const sampleTrackingLink = `https://activqms.com/t/${sampleTrackingData}`;

    const smsPreview = `${content} ${sampleTrackingLink}`;
    const chatPreview = content;

    setPreviewData({
      smsPreview,
      chatPreview,
      trackingData: sampleTrackingData,
      trackingLink: sampleTrackingLink,
      fullContent: smsPreview,
      isValid: content.trim().length > 0 && content.length <= 140,
    });
    setLastUpdated(new Date());
  }, [content]);

  // Generate preview with or without tracking
  const generatePreviewData = useCallback(() => {
    if (messageId && conversationId && userId) {
      // Generate with real tracking data
      generatePreview({
        variables: {
          messageId,
          conversationId,
          userId,
        },
      });
    } else {
      // Generate fallback preview
      generateFallbackPreview();
    }
  }, [
    messageId,
    conversationId,
    userId,
    generatePreview,
    generateFallbackPreview,
  ]);

  // Handle real-time content updates
  useEffect(() => {
    if (!enableRealTimeUpdates) return undefined;

    // Clear existing timeout
    if (updateTimeout) {
      clearTimeout(updateTimeout);
    }

    // Set new timeout for debounced update
    const timeout = setTimeout(() => {
      generatePreviewData();
    }, updateInterval);

    setUpdateTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [content, enableRealTimeUpdates, updateInterval, generatePreviewData]);

  // Manual refresh function
  const refreshPreview = useCallback(() => {
    generatePreviewData();
  }, [generatePreviewData]);

  // Memoized error message
  const error = useMemo(() => {
    if (queryError) {
      return queryError.message;
    }
    return null;
  }, [queryError]);

  // Initialize preview on mount
  useEffect(() => {
    generatePreviewData();
  }, []); // Only run on mount

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
    };
  }, [updateTimeout]);

  return {
    previewData,
    isGenerating,
    error,
    lastUpdated,
    refreshPreview,
  };
}
